// Hideo - waveform of a voice note: the bars drawn by VoicePlayer.vue (from the decoded audio) and, while recording,
// the live bars of the idea box (src/stores/idea.ts, from the microphone's level).

/** Fine waveform kept for a voice note: the bars shown are made from it for the width available (barsFor, resample) */
export const WAVE_BARS = 400
/** Live bars kept while recording (the newest on the right; the oldest go out on the left) */
export const LIVE_BARS = 120
/** Bar width and gap in pixels */
export const BAR_W = 3, BAR_GAP = 2
/** Lowest bar, so silence still shows a dotted line (0..1) */
export const WAVE_MIN = 0.08

/** Heights (WAVE_MIN..1) of `bars` bars from the samples of a channel: the loudest point of each slice, relative to the
 *  loudest of all (a quiet recording still fills the height) */
export function peaksOf(samples: ArrayLike<number>, bars = WAVE_BARS): number[] {
  const n = samples.length, out: number[] = []
  if (!n) return Array(bars).fill(WAVE_MIN)
  for (let b = 0; b < bars; b++) {
    const from = Math.floor(b * n / bars), to = Math.max(from + 1, Math.floor((b + 1) * n / bars))
    let max = 0
    for (let i = from; i < to && i < n; i++) {
      const v = Math.abs(samples[i])
      if (v > max) max = v
    }
    out.push(max)
  }
  const top = Math.max(...out)
  return out.map(v => (top ? Math.max(WAVE_MIN, v / top) : WAVE_MIN))
}

/** How many bars fit in a width (pixels), between 12 and 160 */
export const barsFor = (width: number): number => Math.max(12, Math.min(160, Math.floor((width + BAR_GAP) / (BAR_W + BAR_GAP))))

/** The fine waveform brought to n bars: the highest of each slice */
export function resample(peaks: readonly number[], n: number): number[] {
  if (!peaks.length) return Array(n).fill(WAVE_MIN)
  const out: number[] = []
  for (let b = 0; b < n; b++) {
    const from = Math.floor(b * peaks.length / n), to = Math.max(from + 1, Math.floor((b + 1) * peaks.length / n))
    out.push(Math.max(...peaks.slice(from, Math.min(to, peaks.length))))
  }
  return out
}

/** Level (WAVE_MIN..1) of a slice of microphone input (AnalyserNode.getByteTimeDomainData: 128 = silence) */
export function levelOf(bytes: ArrayLike<number>): number {
  let sum = 0
  for (let i = 0; i < bytes.length; i++) {
    const v = (bytes[i] - 128) / 128
    sum += v * v
  }
  const rms = bytes.length ? Math.sqrt(sum / bytes.length) : 0
  // Speech is quiet in RMS terms: amplified so a normal voice reaches about two thirds of the height
  return Math.min(1, Math.max(WAVE_MIN, rms * 4))
}

/** "m:ss" of a time in seconds (0:00 for anything not finite) */
export const waveClock = (sec: number): string => {
  const s = Number.isFinite(sec) && sec > 0 ? Math.floor(sec) : 0
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** The browser's AudioContext (older Safari: webkitAudioContext); null without Web Audio */
export function audioContextClass(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  return window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext || null
}
